import { api } from './api';

export interface Course {
  _id: string;
  schoolId: string | {
    _id: string;
    name: string;
    location?: string;
    city?: string;
    country?: string;
    slug?: string;
  };
  name: string;
  slug: string;
  type: 'COURSE' | 'RETREAT' | 'WORKSHOP' | 'TRAINING' | 'CLASS';
  description: string;
  shortDescription?: string;
  price?: number;
  priceRange?: {
    min: number;
    max: number;
    currency: string;
  };
  currency: string;
  duration: number;
  maxStudents?: number;
  minStudents?: number;
  difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'ALL_LEVELS';
  yogaStyles?: string[];
  schedule?: {
    startDate?: string;
    endDate?: string;
    startDay?: number;
    activeMonths?: number[];
    monthStartDays?: { month: number; year?: number; startDay: number }[];
    dateRanges?: { startDate: string; endDate: string }[];
    timeSlots?: {
      day: string;
      startTime: string;
      endTime: string;
      label?: string;
    }[];
  };
  inclusions?: string[];
  requirements?: string[];
  whoIsThisFor?: string[];
  courseHighlights?: string[];
  whatYouWillGain?: string[];
  curriculum?: {
    title: string;
    items: string[];
  }[];
  timetable?: string[];
  faq?: { question: string; answer: string }[];
  media?: {
    images: string[];
    videos: string[];
  };
  accommodation?: {
    available: boolean;
    rooms: {
      roomId?: string;
      type: 'PRIVATE' | 'DOUBLE' | 'TRIPLE' | 'QUADRUPLE' | 'CUSTOM';
      capacity: number;
      price: number;
      bookingAmount?: number;
      amenities: string[];
      description?: string;
      images?: string[];
      videos?: string[];
    }[];
    mealPlan?: {
      included: boolean;
      type: 'VEGETARIAN' | 'VEGAN' | 'OMNIVORE' | 'CUSTOM';
      description?: string;
    };
  };
  images?: string[];
  yogaAllianceCertified?: boolean;
  isActive: boolean;
  isFeatured: boolean;
  rating: number;
  totalReviews: number;
  totalBookings: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export interface CoursesResponse {
  courses: Course[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface SearchCoursesParams {
  search?: string;
  location?: string;
  yogaStyles?: string[];
  yogaAllianceCertified?: 'yes' | 'no' | 'both';
  page?: number;
  limit?: number;
  sort?: 'name' | 'price' | 'duration' | 'rating' | 'createdAt';
  order?: 'asc' | 'desc';
  priceMin?: number;
  priceMax?: number;
  difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'ALL_LEVELS';
  type?: 'COURSE' | 'RETREAT' | 'WORKSHOP' | 'TRAINING' | 'CLASS';
}

export const courseService = {
  async searchCourses(params: SearchCoursesParams): Promise<ApiResponse<CoursesResponse>> {
    try {
      const searchParams = {
        search: params.search || '',
        location: params.location || '',
        yogaStyles: params.yogaStyles || [],
        yogaAllianceCertified: params.yogaAllianceCertified || 'both',
        page: params.page || 1,
        limit: params.limit || 12,
        sort: params.sort || 'createdAt',
        order: params.order || 'desc',
        isActive: 'true',
        ...(params.priceMin !== undefined && { priceMin: params.priceMin }),
        ...(params.priceMax !== undefined && { priceMax: params.priceMax }),
        ...(params.difficulty && { difficulty: params.difficulty }),
        ...(params.type && { type: params.type }),
      };
      const response = await api.post('/courses/search', searchParams);
      return response.data;
    } catch (error: any) {
      console.error('Error searching courses:', error);
      throw new Error(error.response?.data?.message || 'Failed to search courses');
    }
  },

  async getSchoolCourses(schoolId: string, page: number = 1, limit: number = 50): Promise<ApiResponse<CoursesResponse>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      const response = await api.get(`/courses/school/${schoolId}?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching courses:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch courses');
    }
  },

  async getCourseById(courseId: string): Promise<ApiResponse<{ course: Course }>> {
    try {
      const response = await api.get(`/courses/${courseId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching course:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch course');
    }
  },

  async getCourseBySlug(courseSlug: string): Promise<ApiResponse<{ course: Course }>> {
    try {
      const response = await api.get(`/courses/${courseSlug}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching course by slug:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch course');
    }
  },

  async getCourseReviews(
    courseId: string,
    page: number = 1,
    limit: number = 10
  ): Promise<ApiResponse<{ reviews: CourseReview[]; pagination: { page: number; limit: number; total: number } }>> {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      const response = await api.get(`/courses/${courseId}/reviews?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching course reviews:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch reviews');
    }
  },

  async createCourseReview(
    courseId: string,
    data: { rating: number; comment?: string }
  ): Promise<ApiResponse<{ review: CourseReview }>> {
    try {
      const response = await api.post(`/courses/${courseId}/reviews`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error submitting review:', error);
      throw new Error(error.response?.data?.message || 'Failed to submit review');
    }
  },
};

export interface CourseReview {
  _id: string;
  courseId: string;
  userId: string | { _id: string; firstName?: string; lastName?: string };
  rating: number;
  comment?: string;
  createdAt: string;
  updatedAt: string;
}







